/** Helpers for the Super Admin billing pages. Amounts are paise (INR minor units). */

const IST = 'Asia/Kolkata';

export function formatInr(paise, { decimals = true } = {}) {
  if (paise == null || Number.isNaN(Number(paise))) return '—';
  const rupees = Number(paise) / 100;
  return `₹${rupees.toLocaleString('en-IN', {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  })}`;
}

export function formatDate(value, { withTime = false } = {}) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

/** "2026-10" -> "Oct 2026" */
export function formatMonth(key) {
  const [year, month] = String(key || '').split('-').map(Number);
  if (!year || !month) return key || '—';
  return new Date(Date.UTC(year, month - 1, 15)).toLocaleString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** YYYY-MM-DD (IST calendar date) for a date input. */
export function toDateInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  return parts;
}

/** A date input (YYYY-MM-DD) as the end of that day in IST, ISO string. */
export function endOfIstDay(dateInput) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateInput || ''))) return null;
  const date = new Date(`${dateInput}T23:59:59.000+05:30`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function refundableRemaining(payment) {
  if (!payment) return 0;
  const amount = Number(payment.amount) || 0;
  const refunded = Number(payment.amountRefunded) || 0;
  return Math.max(0, amount - refunded);
}

export function canRefund(payment) {
  if (!payment) return false;
  return ['captured', 'refunded'].includes(payment.status) && refundableRemaining(payment) > 0;
}

/**
 * Validate a refund form. Empty amount means "refund everything left".
 * @returns {{ amount: number|null, error: string|null }}
 */
export function parseRefundInput({ rupees, reason }, remainingPaise) {
  const text = String(rupees ?? '').trim();
  const why = String(reason ?? '').trim();
  if (why.length < 3) return { amount: null, error: 'Give a reason (at least 3 characters).' };
  if (!text) return { amount: null, error: null };
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return { amount: null, error: 'Enter an amount like 499 or 499.50.' };
  const paise = Math.round(Number(text) * 100);
  if (paise <= 0) return { amount: null, error: 'Amount must be more than ₹0.' };
  if (paise > remainingPaise) return { amount: null, error: `At most ${formatInr(remainingPaise)} can be refunded.` };
  return { amount: paise === remainingPaise ? null : paise, error: null };
}

/** Status shown to admins: stored status plus a pending-cancel marker. */
export function subscriptionStatusLabel(subscription) {
  if (!subscription) return 'unknown';
  const status = subscription.effectiveStatus || subscription.status;
  if (status === 'active' && subscription.cancelAtPeriodEnd) return 'cancelling';
  return status;
}

export function payerLabel(payerType) {
  if (payerType === 'partner_customer') return 'Partner-billed';
  if (payerType === 'platform_partner') return 'Partner (wholesale)';
  return 'Direct';
}

export function isFreePlan(subscription) {
  const plan = subscription?.plan;
  if (!plan) return false;
  return plan.code === 'free' || (Number(plan.priceMonthly) === 0 && Number(plan.priceYearly) === 0);
}

/**
 * Lifecycle actions an admin may take on a subscription, in display order.
 * Mirrors the backend state machine closely enough to hide impossible actions;
 * the backend still has the final say.
 */
export function availableActions(subscription) {
  if (!subscription) return [];
  const status = subscription.status;
  const free = isFreePlan(subscription);
  const actions = [];

  if (status === 'active') actions.push('changePlan');
  if (['draft', 'trial', 'grace', 'suspended', 'cancelled', 'expired'].includes(status)) actions.push('activate');
  if (['trial', 'cancelled', 'expired'].includes(status) || (status === 'active' && free)) actions.push('extendTrial');
  if (status === 'active' && subscription.cancelAtPeriodEnd) actions.push('resume');
  if (status === 'active' && !free && !subscription.cancelAtPeriodEnd) actions.push('cancelAtPeriodEnd');
  if (status === 'trial' || status === 'grace' || (status === 'active' && !free)) actions.push('cancelNow');
  if (['active', 'grace'].includes(status)) actions.push('suspend');
  if (['grace', 'suspended', 'cancelled', 'expired'].includes(status)) actions.push('reactivate');
  return actions;
}

/** A trial on Free means "try this paid plan", so the plan must be chosen. */
export function trialNeedsPlan(subscription) {
  return subscription?.status === 'active' && isFreePlan(subscription);
}

export const ACTION_LABELS = Object.freeze({
  changePlan: 'Change plan or dates',
  activate: 'Activate on a plan',
  extendTrial: 'Extend or start trial',
  resume: 'Undo scheduled cancellation',
  cancelAtPeriodEnd: 'Cancel at period end',
  cancelNow: 'Cancel now',
  suspend: 'Suspend',
  reactivate: 'Reactivate',
});

/**
 * Build the lifecycle request for an action form.
 * @returns {{ path: string, body: object, error: string|null }}
 */
export function buildLifecycleRequest(action, subscription, form = {}) {
  const reason = String(form.reason || '').trim();
  const base = { subscriptionId: subscription?.id, reason };
  if (!subscription?.id) return { path: null, body: null, error: 'No subscription selected.' };
  if (reason.length < 3) return { path: null, body: null, error: 'Give a reason (at least 3 characters). It is saved in the history.' };

  switch (action) {
    case 'changePlan':
    case 'activate': {
      if (!form.planId) return { path: null, body: null, error: 'Choose a plan.' };
      const cycle = form.billingCycle || 'monthly';
      const fields = { planId: form.planId, billingCycle: cycle };
      if (cycle !== 'none') {
        const end = endOfIstDay(form.subscriptionEnd);
        if (!end) return { path: null, body: null, error: 'Choose the date the plan is paid until.' };
        if (new Date(end).getTime() <= Date.now()) return { path: null, body: null, error: 'The end date must be in the future.' };
        fields.subscriptionEnd = end;
      } else {
        fields.subscriptionEnd = null;
      }
      if (action === 'changePlan') {
        return { method: 'patch', path: `/subscriptions/${subscription.id}`, body: { ...fields, reason }, error: null };
      }
      return { path: '/subscription/activate', body: { ...base, ...fields }, error: null };
    }
    case 'extendTrial': {
      const days = Number(form.days);
      if (!Number.isInteger(days) || days < 1 || days > 365) {
        return { path: null, body: null, error: 'Days must be a whole number from 1 to 365.' };
      }
      const body = { ...base, days };
      if (trialNeedsPlan(subscription)) {
        if (!form.planId) return { path: null, body: null, error: 'Choose the paid plan to trial.' };
        body.planId = form.planId;
      }
      return { path: '/subscription/extend-trial', body, error: null };
    }
    case 'resume':
      return { path: '/subscription/resume', body: base, error: null };
    case 'cancelAtPeriodEnd':
      return { path: '/subscription/cancel', body: { ...base, atPeriodEnd: true }, error: null };
    case 'cancelNow':
      return { path: '/subscription/cancel', body: { ...base, atPeriodEnd: false }, error: null };
    case 'suspend':
      return { path: '/subscription/suspend', body: base, error: null };
    case 'reactivate':
      return { path: '/subscription/reactivate', body: base, error: null };
    default:
      return { path: null, body: null, error: 'Unknown action.' };
  }
}

const AUTOPAY_STATUS_LABELS = {
  created: 'Awaiting approval',
  authenticated: 'Authorized',
  active: 'Active',
  pending: 'Retrying',
  halted: 'Halted',
  paused: 'Paused',
  cancelled: 'Cancelled',
  completed: 'Completed',
  expired: 'Expired',
};

const AUTOPAY_BADGE = {
  created: 'pending',
  authenticated: 'authorized',
  active: 'active',
  pending: 'grace',
  halted: 'failed',
  paused: 'grace',
  cancelled: 'cancelled',
  completed: 'inactive',
  expired: 'expired',
};

const AUTOPAY_END_REASONS = {
  customer: 'Turned off by the customer',
  admin: 'Turned off by Super Admin',
  halted: 'Stopped after failed payments',
  plan_changed: 'Plan or cycle changed',
  plan_cancelled: 'Plan cancelled',
  abandoned: 'Never approved',
  replaced: 'Replaced by a new setup',
};

const AUTOPAY_METHODS = { upi: 'UPI AutoPay', card: 'Card', emandate: 'Bank mandate', nach: 'Bank mandate' };

/** { label, badge } for an autopay mandate status; `badge` is a StatusBadge class suffix. */
export function autopayStatus(status) {
  const key = String(status || '').toLowerCase();
  return { label: AUTOPAY_STATUS_LABELS[key] || status || '—', badge: AUTOPAY_BADGE[key] || 'inactive' };
}

export function autopayEndReasonLabel(reason) {
  if (!reason) return null;
  return AUTOPAY_END_REASONS[reason] || reason;
}

export function autopayMethodLabel(method) {
  if (!method) return null;
  return AUTOPAY_METHODS[String(method).toLowerCase()] || method;
}
